import React, { useState } from 'react';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import MultiSelectDropdown from './MultiSelectDropdown.js';
import {
  FILTER_INPUT_CLASS,
  FILTER_LABEL_CLASS,
  FILTER_PANEL_CLASS,
} from './filterStyles.js';

const MAX_VISIBLE_FILTERS = 5;

const FilterPanel = ({
  filters = [],
  values = {},
  onChange,
  onClear,
  title = 'Filters',
  className = '',
  collapsible = false,
  defaultExpanded = true,
  clearLabel = 'Clear',
  extraContent,
}) => {
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [showExtraFilters, setShowExtraFilters] = useState(false);

  const visibleFilters = filters.filter((f) => f.visible !== false);
  const count = visibleFilters.length;
  const hasExtraFilters = count > MAX_VISIBLE_FILTERS;
  const primaryFilters = hasExtraFilters ? visibleFilters.slice(0, MAX_VISIBLE_FILTERS) : visibleFilters;
  const extraFilterItems = hasExtraFilters ? visibleFilters.slice(MAX_VISIBLE_FILTERS) : [];

  const isFilterActive = (filter) => {
    const value = values[filter.key];
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== null && value !== '';
  };

  const hasActiveFilters = visibleFilters.some(isFilterActive);

  const handleClear = () => {
    setShowExtraFilters(false);
    onClear?.();
  };

  const handleChange = (key, value) => {
    onChange?.({ ...values, [key]: value });
  };

  const renderFilterInput = (filter) => {
    const value = values[filter.key] ?? (filter.type === 'multiselect' ? [] : '');

    if (filter.type === 'select') {
      return (
        <select
          value={value}
          onChange={(e) => handleChange(filter.key, e.target.value)}
          className={FILTER_INPUT_CLASS}
        >
          <option value="">{filter.placeholder || `All ${filter.label}`}</option>
          {(filter.options || []).map((option) => (
            <option key={option.value ?? option} value={option.value ?? option}>
              {option.label ?? option}
            </option>
          ))}
        </select>
      );
    }

    if (filter.type === 'multiselect') {
      return (
        <MultiSelectDropdown
          options={filter.options || []}
          selected={Array.isArray(value) ? value : []}
          onChange={(val) => handleChange(filter.key, val)}
          placeholder={filter.placeholder || `All ${filter.label}`}
        />
      );
    }

    if (filter.type === 'date') {
      return (
        <input
          type="date"
          value={value}
          onChange={(e) => handleChange(filter.key, e.target.value)}
          className={FILTER_INPUT_CLASS}
        />
      );
    }

    return (
      <input
        type={filter.type || 'text'}
        placeholder={filter.placeholder || `Search ${filter.label}...`}
        value={value}
        onChange={(e) => handleChange(filter.key, e.target.value)}
        className={FILTER_INPUT_CLASS}
      />
    );
  };

  const renderFilterField = (filter) => (
    <div key={filter.key} className={filter.colSpan ? `sm:col-span-${filter.colSpan}` : ''}>
      <label className={FILTER_LABEL_CLASS}>{filter.label}</label>
      {renderFilterInput(filter)}
    </div>
  );

  const showFilters = collapsible && !hasExtraFilters ? expanded : true;

  const xlColsMap = {
    1: 'xl:grid-cols-1',
    2: 'xl:grid-cols-2',
    3: 'xl:grid-cols-3',
    4: 'xl:grid-cols-4',
    5: 'xl:grid-cols-5',
    6: 'xl:grid-cols-6',
  };
  const xlCols = xlColsMap[Math.min(Math.max(primaryFilters.length, 1), 6)] || 'xl:grid-cols-6';

  return (
    <div className={`${FILTER_PANEL_CLASS} ${className}`}>
      <div className="flex items-center justify-between mb-0 sm:mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-brand shrink-0" />
          <h3 className="text-base sm:text-lg font-semibold text-secondary">{title}</h3>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {collapsible && !hasExtraFilters && (
            <button
              type="button"
              onClick={() => setExpanded(!expanded)}
              className="hidden sm:flex items-center gap-1 text-brand font-medium text-sm hover:text-brand-hover transition-colors"
            >
              {expanded ? (
                <>
                  <ChevronUp className="w-4 h-4" /> Less Filters
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" /> More Filters
                </>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="sm:hidden text-brand font-medium text-sm"
          >
            {showMobileFilters ? 'Hide' : 'Show'} Filters
          </button>
          {hasExtraFilters && (
            <button
              type="button"
              onClick={() => setShowExtraFilters(!showExtraFilters)}
              className="hidden sm:flex items-center justify-center w-8 h-8 rounded-input border border-gray-200 text-brand hover:bg-brand-light hover:border-brand/30 transition-colors shrink-0"
              title={showExtraFilters ? 'Hide additional filters' : `Show ${extraFilterItems.length} more filters`}
              aria-label={showExtraFilters ? 'Hide additional filters' : 'Show additional filters'}
              aria-expanded={showExtraFilters}
            >
              {showExtraFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
          {hasActiveFilters && onClear && (
            <button
              type="button"
              onClick={handleClear}
              className="flex items-center gap-1 text-brand hover:text-brand-hover transition-colors"
              title="Clear all filters"
            >
              <X className="w-4 h-4" />
              <span className="text-sm font-medium">{clearLabel}</span>
            </button>
          )}
        </div>
      </div>

      <div
        className={`${
          collapsible && !hasExtraFilters
            ? showFilters
              ? 'block'
              : 'hidden'
            : showMobileFilters
              ? 'block'
              : 'hidden sm:block'
        }`}
      >
        <div className="relative pt-3 sm:pt-0 border-t sm:border-t-0 border-gray-100">
          <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${xlCols} gap-3 sm:gap-4`}>
            {primaryFilters.map(renderFilterField)}
          </div>

          {hasExtraFilters && showExtraFilters && (
            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${xlColsMap[Math.min(extraFilterItems.length, 6)] || 'xl:grid-cols-6'} gap-3 sm:gap-4 mt-3 pt-3 border-t border-gray-100`}>
              {extraFilterItems.map(renderFilterField)}
            </div>
          )}
        </div>
        {extraContent && <div className="mt-3 sm:mt-4 pt-3 border-t border-gray-100">{extraContent}</div>}
      </div>
    </div>
  );
};

export default FilterPanel;
